import React, { useState, useEffect } from 'react';
import {
  getApplianceAvailability,
  getApplianceThings,
  getApplianceEnergyDaily,
} from '../../../../services';
import { DeltaPill } from '../../../../components';
import * as s from './styled-plant-availability';

// Coage pra numero finito ou null (DeltaPill exige number; null = nao mostra).
const num = (v) =>
  v != null && Number.isFinite(Number(v)) ? Number(v) : null;

// Mapeia o periodo do toggle pra nº de dias (pro fator de capacidade).
const DAYS_BY_PERIOD = { day: 1, week: 7, month: 30 };

// KPIs de disponibilidade REAL da usina no periodo (das transicoes de
// status do backend): % disponivel, MTBF (tempo medio entre falhas), MTTR
// (tempo medio de reparo) e nº de falhas. Substitui o proxy de "uptime de
// sessao" por dado historico de verdade.

const PERIODS = [
  { key: 'day', label: 'Dia' },
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mês' },
];

// Duracao legivel a partir de segundos.
const fmtDuration = (sec) => {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  const s = Math.max(0, Math.floor(Number(sec)));
  if (s < 60) return `${s}s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    const rmin = min % 60;
    return rmin ? `${hr}h ${rmin}min` : `${hr}h`;
  }
  const day = Math.floor(hr / 24);
  const rhr = hr % 24;
  return rhr ? `${day}d ${rhr}h` : `${day}d`;
};

// Cor da disponibilidade por faixa (semantica de SLA).
const availColor = (pct) => {
  if (pct == null) return 'var(--cco-ink-mute)';
  if (pct >= 99) return 'var(--cco-green)';
  if (pct >= 90) return 'var(--cco-orange)';
  return 'var(--cco-red)';
};

const PlantAvailability = ({ applianceId, accent, refreshKey }) => {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Fator de capacidade (so UCHA): geracao / (nominal * horas). null = sem
  // dado de placa cadastrado ou nao aplicavel.
  const [capacityFactor, setCapacityFactor] = useState(null);
  const isUcha = accent === 'ucha';

  useEffect(() => {
    if (applianceId == null) return undefined;
    let active = true;
    setLoading(true);
    setError(false);
    getApplianceAvailability(applianceId, period)
      .then((res) => {
        if (!active) return;
        setData((res && res.data) || res || null);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [applianceId, period]);

  // Fator de capacidade (UCHA): soma a placa (potenciaNominal) das maquinas
  // e a energia gerada no periodo; CF = energia / (nominal * horas) * 100.
  useEffect(() => {
    if (applianceId == null || !isUcha) {
      setCapacityFactor(null);
      return undefined;
    }
    let active = true;
    const days = DAYS_BY_PERIOD[period] || 7;
    Promise.all([
      getApplianceThings(applianceId).catch(() => null),
      getApplianceEnergyDaily(applianceId, days).catch(() => null),
    ]).then(([thingsRes, energyRes]) => {
      if (!active) return;
      const appliance = (thingsRes && thingsRes.data) || thingsRes || {};
      const things = appliance.thing || appliance.things || [];
      const nominalSum = things.reduce(
        (acc, t) => acc + (Number(t.potenciaNominal) || 0),
        0,
      );
      const energyData =
        (energyRes &&
          ((energyRes.result && energyRes.result.data) || energyRes.data)) ||
        [];
      const totalEnergy = Array.isArray(energyData)
        ? energyData.reduce((acc, d) => acc + Math.abs(Number(d.energy) || 0), 0)
        : 0;
      if (nominalSum > 0 && days > 0) {
        setCapacityFactor((totalEnergy / (nominalSum * days * 24)) * 100);
      } else {
        setCapacityFactor(null);
      }
    });
    return () => {
      active = false;
    };
  }, [applianceId, period, isUcha, refreshKey]);

  const pct =
    data && data.availabilityPct != null ? Number(data.availabilityPct) : null;

  // Periodo sem nenhuma transicao de status registrada (disponibilidade
  // nula + 0 falhas) => mostra estado saudavel em vez da fileira de "—",
  // que passava impressao de erro. Fator de capacidade (UCHA) entra inline
  // se a placa estiver cadastrada.
  const noStops =
    !loading && !error && pct == null && !(data && data.failureCount);

  return (
    <s.Wrap>
      <s.PeriodToggle>
        {PERIODS.map((p) => (
          <s.PeriodBtn
            key={p.key}
            type="button"
            $active={period === p.key}
            $accent={accent}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </s.PeriodBtn>
        ))}
      </s.PeriodToggle>

      {error ? (
        <s.Empty>Não foi possível carregar a disponibilidade.</s.Empty>
      ) : noStops ? (
        <s.Ok>
          {/* lucide:check-circle */}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <path d="m9 11 3 3L22 4" />
          </svg>
          Sem paradas registradas no período · 0 falhas
          {isUcha && capacityFactor != null && (
            <> · fator de capacidade {capacityFactor.toFixed(0)}%</>
          )}
        </s.Ok>
      ) : (
        <s.Kpis>
          <s.Kpi>
            <s.KpiLabel title="Disponibilidade = tempo rodando / tempo total no período (das transições de status). 100% = sem paradas.">
              Disponibilidade
            </s.KpiLabel>
            <s.KpiValue style={{ color: availColor(pct) }}>
              {loading || pct == null ? '—' : `${pct.toFixed(1)}`}
              {!loading && pct != null && <small>%</small>}
            </s.KpiValue>
            {/* Comparacao vs periodo anterior — subida e boa (mais
                disponibilidade = melhor). */}
            <DeltaPill
              small
              current={num(pct)}
              previous={num(data && data.previousAvailabilityPct)}
            />
          </s.Kpi>
          <s.Sep />
          <s.Kpi>
            <s.KpiLabel title="MTBF — tempo médio entre falhas">
              Tempo entre falhas
            </s.KpiLabel>
            <s.KpiValue title="Em média, a usina opera este tempo entre uma falha e a próxima (MTBF)">
              {loading ? '—' : fmtDuration(data && data.mtbfSeconds)}
            </s.KpiValue>
            {/* MTBF maior = melhor (mais tempo operando entre falhas). */}
            <DeltaPill
              small
              current={num(data && data.mtbfSeconds)}
              previous={num(data && data.previousMtbfSeconds)}
            />
          </s.Kpi>
          <s.Sep />
          <s.Kpi>
            <s.KpiLabel title="MTTR — tempo médio de reparo">
              Tempo de reparo
            </s.KpiLabel>
            <s.KpiValue title="Em média, cada parada leva este tempo até voltar a operar (MTTR)">
              {loading ? '—' : fmtDuration(data && data.mttrSeconds)}
            </s.KpiValue>
            {/* MTTR menor = melhor (conserta mais rapido) -> invertSemantic. */}
            <DeltaPill
              small
              invertSemantic
              current={num(data && data.mttrSeconds)}
              previous={num(data && data.previousMttrSeconds)}
            />
          </s.Kpi>
          <s.Sep />
          <s.Kpi>
            <s.KpiLabel>Falhas</s.KpiLabel>
            <s.KpiValue
              style={{
                color:
                  data && data.failureCount > 0
                    ? 'var(--cco-red)'
                    : 'var(--cco-ink)',
              }}
            >
              {loading ? '—' : (data && data.failureCount) || 0}
            </s.KpiValue>
            {/* Menos falhas = melhor -> invertSemantic. */}
            <DeltaPill
              small
              invertSemantic
              current={num(data && data.failureCount)}
              previous={num(data && data.previousFailureCount)}
            />
          </s.Kpi>
          {isUcha && (
            <>
              <s.Sep />
              <s.Kpi>
                <s.KpiLabel>Fator de capacidade</s.KpiLabel>
                <s.KpiValue title="Geração no período / (potência nominal × horas). Requer a placa cadastrada.">
                  {capacityFactor == null ? '—' : capacityFactor.toFixed(0)}
                  {capacityFactor != null && <small>%</small>}
                </s.KpiValue>
              </s.Kpi>
            </>
          )}
        </s.Kpis>
      )}
    </s.Wrap>
  );
};

export default PlantAvailability;
